import { merge } from 'lodash';
import colors from '@/themes/colors/colors';
import { darkenColor, lightenColor } from '@/themes/colorUtils/colorUtils';
import themeInterface from '@/themes/themes/themeInterface';

const sherbetTheme = {
  name: 'sherbet',
  background: {
    bg: lightenColor(colors.spindle, 0.7),
    text: darkenColor(colors.norticBlue, 0.35),
    textDiscrete: colors.suvaGrey,
    textAltColor: colors.almostBlack,
    variant: {
      bg: lightenColor(colors.lavender, 0.4),
      text: colors.almostBlack,
      textDiscrete: colors.suvaGrey,
      textAltColor: darkenColor(colors.lavender, 0.9),
    },
  },
  border: {
    color: lightenColor(colors.suvaGrey, 0.6),
    variant: {
      color: colors.almostBlack,
    },
    danger: {
      color: lightenColor(colors.sunsetOrange, 0.7),
    },
    highlight: {
      color: lightenColor(colors.norticBlue, 0.8),
    },
  },
  shadow: {
    color: darkenColor(colors.white, 0.53),
    variant: {
      color: colors.almostBlack,
    },
  },
  primary: {
    bg: colors.white,
    text: colors.almostBlack,
    textDiscrete: darkenColor(colors.suvaGrey, 0.35),
    textAltColor: colors.norticBlue,
    variant: {
      bg: darkenColor(colors.white, 0.05),
      text: colors.almostBlack,
      textDiscrete: colors.suvaGrey,
      textAltColor: darkenColor(colors.norticBlue, 0.1),
    },
  },
  accent: {
    bg: colors.norticBlue,
    text: colors.white,
    textDiscrete: colors.white,
    textAltColor: colors.white,
    variant: {
      bg: darkenColor(colors.norticBlue, 0.2),
      text: colors.white,
      textDiscrete: colors.white,
      textAltColor: colors.white,
    },
  },
  accentComplementary: {
    bg: colors.internationalOrange,
    text: colors.white,
    textDiscrete: colors.white,
    textAltColor: colors.almostBlack,
    variant: {
      bg: darkenColor(colors.internationalOrange, 0.05),
      text: colors.white,
      textDiscrete: colors.red,
      textAltColor: lightenColor(colors.norticBlue, 0.4),
    },
  },
  success: {
    bg: lightenColor(colors.forestGreen, 0.85),
    text: colors.almostBlack,
    textDiscrete: colors.white,
    textAltColor: colors.forestGreen,
    variant: {
      bg: lightenColor(colors.forestGreen, 0.6),
      text: colors.almostBlack,
      textDiscrete: colors.white,
      textAltColor: darkenColor(colors.forestGreen, 0.3),
    },
  },
  danger: {
    bg: lightenColor(colors.sunsetOrange, 0.85),
    text: colors.almostBlack,
    textDiscrete: colors.white,
    textAltColor: colors.sunsetOrange,
    variant: {
      bg: lightenColor(colors.sunsetOrange, 0.75),
      text: colors.almostBlack,
      textDiscrete: colors.white,
      textAltColor: colors.sunsetOrange,
    },
  },
  warn: {
    bg: lightenColor(colors.darkTangerine, 0.8),
    text: colors.almostBlack,
    textDiscrete: colors.white,
    textAltColor: darkenColor(colors.darkTangerine, 0.2),
    variant: {
      bg: lightenColor(colors.darkTangerine, 0.7),
      text: colors.almostBlack,
      textDiscrete: colors.white,
      textAltColor: darkenColor(colors.darkTangerine, 0.2),
    },
  },
  info: {
    bg: lightenColor(colors.deepSkyBlue, 0.8),
    text: colors.almostBlack,
    textDiscrete: colors.white,
    textAltColor: darkenColor(colors.deepSkyBlue, 0),
    variant: {
      bg: lightenColor(colors.suvaGrey, 0.7),
      text: colors.almostBlack,
      textDiscrete: colors.white,
      textAltColor: darkenColor(colors.suvaGrey, 0.3),
    },
  },
  contrast: {
    bg: colors.almostBlack,
    text: colors.norticBlue,
    textDiscrete: colors.suvaGrey,
    textAltColor: colors.white,
    variant: {
      bg: lightenColor(colors.almostBlack, 0.2),
      text: colors.almostBlack,
      textDiscrete: colors.white,
      textAltColor: colors.white,
    },
  },
  menu: {
    moduleCategory: {
      text: darkenColor(colors.norticBlue, 0.4),
    },
    module: {
      text: darkenColor(colors.suvaGrey, 0.3),
      highlightBg: lightenColor(colors.norticBlue, 0.9),
      activeBg: lightenColor(colors.norticBlue, 0.92),
    },
    subModule: {
      text: darkenColor(colors.suvaGrey, 0.3),
      activeText: colors.norticBlue,
    },
  },
  subMenu: {
    opacity: '0.1',
  },
  input: {
    bg: darkenColor(colors.white, 0.02),
    border: lightenColor(colors.suvaGrey, 0.7),
    text: colors.almostBlack,
    active: {
      bg: lightenColor(colors.norticBlue, 0.92),
      border: colors.norticBlue,
    },
    danger: {
      bg: lightenColor(colors.sunsetOrange, 0.9),
      border: lightenColor(colors.sunsetOrange, 0.4),
    },
    warn: {
      bg: lightenColor(colors.darkTangerine, 0.9),
      border: lightenColor(colors.darkTangerine, 0.4),
    },
    hover: {
      bg: darkenColor(colors.white, 0.04),
      border: lightenColor(colors.suvaGrey, 0.5),
    },
    alt: {
      text: darkenColor(colors.white, 0.5),
    },
  },
  misc: {
    salesTools: {
      socialSelling: colors.deepSkyBlue,
      trafficBooster: colors.sunsetOrange,
      outdoorAds: colors.darkTangerine,
      webAds: colors.blueViolet,
    },
    landingPage: {
      createEventIcon: colors.forestGreen,
      comfortPackageIcon: colors.deepSkyBlue,
      supportIcon: colors.wildWatermelon,
      myEventsIcon: colors.royalBlue,
      salesToolsIcon: colors.blueViolet,
      addProductIcon: colors.mountainMeadow,
      norticLivePaymentsIcon: colors.norticBlue,
      discoverNorticApps: colors.norticBlue,
    },
    productVariantColors: {
      0: colors.blueViolet,
      1: darkenColor(colors.darkTangerine, 0.1),
      2: colors.forestGreen,
    },
  },
};

export default Object.freeze(merge({}, themeInterface, sherbetTheme));
